import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { NavArrowLeft } from 'iconoir-react';
import { contactApi, fetchCsrfCookie, getApiErrorMessage } from '../services/api';
import { PublicFooter } from '../components/PublicFooter';

interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
  website: string; // honeypot
}

const initialState: FormState = { name: '', email: '', subject: '', message: '', website: '' };

export const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(initialState);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = 'Le nom est requis.';
    if (!form.email.trim()) {
      errors.email = "L'adresse email est requise.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errors.email = 'Adresse email invalide.';
    }
    if (!form.subject.trim()) errors.subject = 'Le sujet est requis.';
    if (!form.message.trim()) errors.message = 'Le message est requis.';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    setGlobalError('');
    setSuccess(false);

    if (!validate()) return;

    setSubmitting(true);
    try {
      // Rafraîchir le cookie CSRF avant l'envoi
      await fetchCsrfCookie();
      await contactApi.send(form);
      setSuccess(true);
      setForm(initialState);
      setFieldErrors({});
    } catch (err: any) {
      const apiErrors = err.response?.data?.errors as Record<string, string[]> | undefined;
      if (apiErrors) {
        const mapped: Record<string, string> = {};
        Object.keys(apiErrors).forEach((key) => {
          mapped[key] = apiErrors[key][0];
        });
        setFieldErrors(mapped);
      }
      if (err.response?.status === 429) {
        setGlobalError(
          'Trop de messages ont été envoyés. Veuillez patienter quelques minutes avant de réessayer.'
        );
      } else {
        setGlobalError(
          getApiErrorMessage(
            err,
            "Une erreur est survenue lors de l'envoi de votre message. Veuillez réessayer."
          )
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      <header className="landing-header">
        <div className="landing-container">
          <div className="landing-nav">
            <Link to="/" className="landing-logo">
              <img src="/assets/ventilio.png" alt="Ventilio" className="landing-logo-img" />
            </Link>
            <button type="button" className="btn-landing-secondary contact-back-btn" onClick={() => navigate('/')}>
              <NavArrowLeft width={18} height={18} strokeWidth={2} />
              Retour à l'accueil
            </button>
          </div>
        </div>
      </header>

      <section className="contact-hero">
        <div className="landing-container">
          <h1>Contactez-nous</h1>
          <p>Une question, une suggestion ? Écrivez-nous, notre équipe vous répond rapidement.</p>
        </div>
      </section>

      <section className="contact-section">
        <div className="landing-container contact-wrap">
          <div className="contact-card">
            <form onSubmit={handleSubmit} className="auth-form" noValidate>
              <div className="contact-honeypot" aria-hidden="true">
                <label htmlFor="contact-website">Site web</label>
                <input
                  id="contact-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={handleChange('website')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact-name">Nom *</label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  placeholder="Jean Dupont"
                  required
                  maxLength={255}
                  value={form.name}
                  onChange={handleChange('name')}
                  disabled={submitting}
                  aria-invalid={!!fieldErrors.name}
                  aria-describedby={fieldErrors.name ? 'contact-name-error' : undefined}
                />
                {fieldErrors.name && (
                  <span id="contact-name-error" className="field-error" role="alert">{fieldErrors.name}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="contact-email">Adresse email *</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  placeholder="jean.dupont@example.com"
                  required
                  maxLength={255}
                  value={form.email}
                  onChange={handleChange('email')}
                  disabled={submitting}
                  aria-invalid={!!fieldErrors.email}
                  aria-describedby={fieldErrors.email ? 'contact-email-error' : undefined}
                />
                {fieldErrors.email && (
                  <span id="contact-email-error" className="field-error" role="alert">{fieldErrors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="contact-subject">Sujet *</label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  required
                  maxLength={255}
                  value={form.subject}
                  onChange={handleChange('subject')}
                  disabled={submitting}
                  aria-invalid={!!fieldErrors.subject}
                  aria-describedby={fieldErrors.subject ? 'contact-subject-error' : undefined}
                />
                {fieldErrors.subject && (
                  <span id="contact-subject-error" className="field-error" role="alert">{fieldErrors.subject}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="contact-message">Message *</label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={6}
                  maxLength={5000}
                  value={form.message}
                  onChange={handleChange('message')}
                  disabled={submitting}
                  aria-invalid={!!fieldErrors.message}
                  aria-describedby={fieldErrors.message ? 'contact-message-error' : undefined}
                />
                {fieldErrors.message && (
                  <span id="contact-message-error" className="field-error" role="alert">{fieldErrors.message}</span>
                )}
              </div>

              {globalError && (
                <div className="auth-error" role="alert">{globalError}</div>
              )}

              {success && (
                <div className="auth-success" role="status">
                  Votre message a bien été envoyé. Merci, nous reviendrons vers vous rapidement.
                </div>
              )}

              <button type="submit" className="btn-landing-primary btn-block" disabled={submitting}>
                {submitting ? 'Envoi en cours...' : 'Envoyer'}
              </button>
            </form>
          </div>

          <p className="contact-back-link">
            <Link to="/">← Retour à l'accueil</Link>
          </p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};
